import { useRef, useEffect, useState } from 'react';
import { useDraft } from '../contexts/DraftContext';
import OpponentPicker from './OpponentPicker';
import {
  getOpponents,
  linkOpponentToDraftSlot,
  unlinkOpponentFromDraftSlot,
  createOpponent,
} from '../services/api';

function calculateTeamIndex(draftType, teamCount, round, pickNumber) {
  if (draftType?.toLowerCase() === 'snake' && round % 2 === 0) {
    return teamCount - pickNumber;
  }
  return pickNumber - 1;
}

/**
 * Snake draft board grid. Rows = rounds, columns = teams.
 * The current-pick cell is highlighted and auto-scrolled into view.
 * The user's team column has a distinct background.
 *
 * @param {boolean} [hideViewerBadge] - Suppresses the "(You)" label on the viewer's own column.
 *   The desktop board has exactly one real viewer (the owner, or -- for Commissioner Mode -- the
 *   specific logged-in participant looking at their own phone), so "(You)" is meaningful there.
 *   DraftTvView casts this same grid to a shared screen with no single "current viewer" -- whoever
 *   happens to be logged into that browser's session would otherwise get their own identity
 *   broadcast to the whole room, which is meaningless (and mildly confusing) on a shared display.
 */
function DraftBoard({ hideViewerBadge = false }) {
  const { draft, picks, draftTeams: contextDraftTeams, aiPicking, mySlotIndex, isOwner } = useDraft();
  const currentCellRef = useRef(null);
  // Local copy of draftTeams so we can update opponents in-place without a full draft refetch
  const [draftTeams, setDraftTeams] = useState(contextDraftTeams);
  const [opponents, setOpponents] = useState([]);
  const [linkingSlot, setLinkingSlot] = useState(null); // slotIndex being async-updated
  // Collapsed by default while a draft is in progress; a completed draft (no single
  // "current" pick in play) defaults to showing full round history immediately.
  const [expanded, setExpanded] = useState(draft?.status === 'completed');

  useEffect(() => {
    if (currentCellRef.current) {
      currentCellRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    }
  }, [draft?.currentRound, draft?.currentPick]);

  // Reset the toggle to the status-based default whenever the user navigates to a
  // different draft (draftId change), instead of carrying over a stale expand/collapse
  // state from the previously viewed draft. Also re-applies when status itself changes
  // (e.g. the currently-viewed draft completes) so the board auto-expands to show full
  // history the moment the last pick lands, without requiring a click or navigation.
  useEffect(() => {
    setExpanded(draft?.status === 'completed');
  }, [draft?.draftId, draft?.status]);

  // Sync local draftTeams whenever context draft changes (e.g., after a pick or re-load)
  useEffect(() => {
    setDraftTeams(contextDraftTeams);
  }, [contextDraftTeams]);

  // For a Commissioner Mode draft, this is the very first time this component (and this fetch)
  // has ever mounted in this browser tab -- it only renders once the draft is completed (see
  // DraftView's showCommissionerView split), right as a 30-90+ minute live session's Cognito ID
  // token is most likely to be mid-refresh. A failure here previously vanished silently (empty
  // "catch") with no retry, permanently showing an empty opponent list even though the user has
  // opponents -- log it and retry once after a short delay, since a transient token-refresh race
  // is very likely to succeed the second time.
  useEffect(() => {
    if (!draft?.draftId) return;
    let cancelled = false;
    let retryTimer = null;

    function fetchOpponents(isRetry) {
      getOpponents()
        .then(list => { if (!cancelled) setOpponents(list); })
        .catch(err => {
          console.error('Failed to load opponents list:', err);
          if (!isRetry) retryTimer = setTimeout(() => fetchOpponents(true), 2000);
        });
    }

    fetchOpponents(false);
    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [draft?.draftId]);

  async function handleLink(slotIndex, opponentId) {
    setLinkingSlot(slotIndex);
    try {
      const updated = await linkOpponentToDraftSlot(draft.draftId, slotIndex, opponentId);
      setDraftTeams(prev => prev.map(t => t.slotIndex === slotIndex ? updated : t));
    } catch {
      // silently ignore
    } finally {
      setLinkingSlot(null);
    }
  }

  async function handleUnlink(slotIndex) {
    setLinkingSlot(slotIndex);
    try {
      const updated = await unlinkOpponentFromDraftSlot(draft.draftId, slotIndex);
      setDraftTeams(prev => prev.map(t => t.slotIndex === slotIndex ? updated : t));
    } catch {
      // silently ignore
    } finally {
      setLinkingSlot(null);
    }
  }

  async function handleCreateAndLink(slotIndex, displayName) {
    setLinkingSlot(slotIndex);
    try {
      const created = await createOpponent({ displayName });
      setOpponents(prev => [...prev, created]);
      const updated = await linkOpponentToDraftSlot(draft.draftId, slotIndex, created.id);
      setDraftTeams(prev => prev.map(t => t.slotIndex === slotIndex ? updated : t));
    } catch {
      // silently ignore
    } finally {
      setLinkingSlot(null);
    }
  }

  if (!draft) return null;

  const { teamCount, rounds, draftType, currentRound, currentPick, status } = draft;
  const ownerSlotIndex = draftTeams.find(t => t.isOwnerSlot)?.slotIndex ?? 0;
  // isOwnerSlot always means the commissioner's slot, not "whoever is viewing this board" -- in
  // Commissioner Mode multiple people view the same board at once, so "my team" has to resolve per
  // viewer via mySlotIndex (see DraftContext). Classic Draft Mode has only ever had one viewer (the
  // owner), so it keeps using ownerSlotIndex unchanged.
  const myTeamSlotIndex = draft.commissionerMode ? mySlotIndex : ownerSlotIndex;
  const isCompleted = status === 'completed';

  const currentTeamIndex = !isCompleted
    ? calculateTeamIndex(draftType, teamCount, currentRound, currentPick)
    : -1;

  // Build [round][slotIndex] -> pick lookup
  const pickMap = {};
  picks.forEach(p => {
    if (!pickMap[p.round]) pickMap[p.round] = {};
    pickMap[p.round][p.slotIndex] = p;
  });

  // draft.currentRound is deliberately only updated once the AI's staggered pick reveal fully
  // completes (see runAiPicks in DraftContext.jsx), so "your turn" logic elsewhere doesn't fire
  // early — but that also left the collapsed board view stuck on the *previous* round for the
  // whole reveal, even once the AI had clearly started picking into the new one. Deriving the
  // displayed round from the picks actually revealed so far lets the view advance immediately,
  // without touching that deliberate timing.
  const latestRevealedRound = picks.length > 0 ? Math.max(...picks.map(p => p.round)) : 0;
  const displayRound = Math.max(currentRound, latestRevealedRound);

  const visibleRounds = expanded
    ? Array.from({ length: rounds }, (_, ri) => ri + 1)
    : [displayRound];

  return (
    <div className='draft-board-scroll'>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 8px 0' }}>
        <button className='btn btn-secondary' onClick={() => setExpanded(e => !e)}>
          {expanded ? 'Show current round only' : 'Show all rounds'}
        </button>
        {!expanded && (
          <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
            Round {displayRound} of {rounds}
          </span>
        )}
      </div>
      <table className='draft-board-table'>
        <thead>
          <tr>
            <th className='round-col'>Rd</th>
            {draftTeams.map((team) => {
              const isUserTeam = team.slotIndex === myTeamSlotIndex;
              // A team's own name is often left at its generic "Team N" default -- most
              // Commissioner Mode guests never bother naming their team -- so surface their guest
              // display name (set at join) whenever there's no other identity shown for this slot
              // already. A linked Opponent profile's name is deliberately NOT duplicated here: it's
              // already shown via OpponentPicker's own trigger label whenever that control is
              // rendered for this slot. Logged-in members have no human-readable name available in
              // this DTO (only their raw account ID), so there's nothing better to show for them.
              const occupantName = team.guestDisplayName || null;
              return (
                <th
                  key={team.slotIndex}
                  className={`team-col ${isUserTeam ? 'user-team-col' : ''}`}
                  title={occupantName ? `${team.teamName} (${occupantName})` : team.teamName}
                >
                  <span className='team-col-name'>{team.teamName}</span>
                  {occupantName && (
                    <span style={{ fontSize: '0.7rem', display: 'block', color: 'var(--text-muted)' }}>
                      {occupantName}
                    </span>
                  )}
                  {!hideViewerBadge && isUserTeam && <span style={{ fontSize: '0.7rem', display: 'block', color: 'var(--text-muted)' }}>(You)</span>}
                  {/* AI-opponent linking is blocked for a live Commissioner Mode draft -- an
                      AI-linked slot has no path to ever pick in a live human draft (see
                      DraftLobbyView). Once the draft is completed there's no more picking to do,
                      so linking an Opponent profile at that point is purely for cross-draft
                      history/tracking -- the same thing classic Draft Mode always allowed. Not
                      commissioner-only: any account-linked participant (a joined member, or a
                      guest who has since used "Save to My Drafts" -- see mySlotIndex in
                      DraftContext) can link an opponent to any OTHER slot, matching what the
                      backend allows (DraftService.requireCanLinkOpponent). */}
                  {!isUserTeam && (!draft.commissionerMode || (isCompleted && (isOwner || mySlotIndex != null))) && (
                    <div style={{ marginTop: 2 }}>
                      <OpponentPicker
                        currentOpponent={team.opponent || null}
                        opponents={opponents}
                        loading={linkingSlot === team.slotIndex}
                        onLink={id => handleLink(team.slotIndex, id)}
                        onUnlink={() => handleUnlink(team.slotIndex)}
                        onCreateAndLink={dname => handleCreateAndLink(team.slotIndex, dname)}
                      />
                    </div>
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {visibleRounds.map((round) => {
            return (
              <tr key={round}>
                <td className='round-col'>{round}</td>
                {Array.from({ length: teamCount }, (_, ti) => {
                  const pick = pickMap[round]?.[ti];
                  const isCurrent = !isCompleted && round === currentRound && ti === currentTeamIndex;
                  const isUserTeam = ti === myTeamSlotIndex;

                  return (
                    <td
                      key={ti}
                      ref={isCurrent ? currentCellRef : null}
                      className={[
                        'draft-cell',
                        isUserTeam ? 'user-team-cell' : '',
                        isCurrent ? 'current-pick-cell' : '',
                      ].filter(Boolean).join(' ')}
                    >
                      {pick ? (
                        <>
                          <div className='pick-player-name'>{pick.playerName}</div>
                          <div className='pick-meta'>
                            <span className={`pos-badge pos-${pick.position}`}>{pick.position}</span>
                            <span className='pick-team-abbr'>{pick.team}</span>
                          </div>
                          {draft.mockDraft && !isUserTeam && (
                            <span className='mock-ai-badge'>AI</span>
                          )}
                        </>
                      ) : isCurrent ? (
                        <div className='on-clock'>
                          {aiPicking && !isUserTeam ? 'AI picking…' : 'On clock'}
                        </div>
                      ) : null}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default DraftBoard;
